import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { accountsApi } from '@/shared/api/endpoints';
import { describeError } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import { Banner, Button, Card } from '@/shared/ui';
import { type Tema, useTheme } from '@/shared/theme/useTheme';

/** Las tres opciones, en el orden en que se leen: lo automático primero. */
const TEMAS: readonly { valor: Tema; etiqueta: string }[] = [
  { valor: 'auto', etiqueta: 'Automático' },
  { valor: 'light', etiqueta: 'Claro' },
  { valor: 'dark', etiqueta: 'Oscuro' },
];

export function AccountPage() {
  const { profile, signOut, forget } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { tema, efectivo, elegir } = useTheme();
  const [busy, setBusy] = useState(false);
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);

  /** CU-14 · el export se descarga desde el navegador; nada sale del dispositivo. */
  const exportData = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await accountsApi.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'fitlog-datos.json';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    setError(null);
    try {
      await accountsApi.deleteAccount();
      // La sesión ya no vale nada del lado del servidor: se olvida sin pedir un cierre más.
      forget();
      navigate('/ingresar', { replace: true });
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <h1>Tu cuenta</h1>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Card>
        <div className="stack" style={{ gap: 'var(--space-1)' }}>
          <span className="small muted">Sesión iniciada como</span>
          <strong>{profile?.email ?? '—'}</strong>
        </div>
        <Link to="/perfil">Editar el perfil</Link>
        <Link to="/objetivos">Ver los objetivos</Link>
        <Link to="/reprocesar">Volver a estimar un rango de fechas</Link>
      </Card>

      {/*
        La apariencia va acá y no en un ícono de la barra: es una preferencia que se toca una vez
        y después no se vuelve a mirar. Un interruptor permanente en la barra le daría el mismo
        peso visual que a registrar una comida, y no es lo mismo.
      */}
      <Card>
        <strong>Apariencia</strong>
        <div className="segmented" role="group" aria-label="Tema">
          {TEMAS.map((opcion) => (
            <button
              key={opcion.valor}
              type="button"
              aria-pressed={tema === opcion.valor}
              onClick={() => elegir(opcion.valor)}
            >
              {opcion.etiqueta}
            </button>
          ))}
        </div>
        <span className="micro muted">
          {tema === 'auto'
            ? `Sigue a tu teléfono, que ahora está en ${efectivo === 'dark' ? 'oscuro' : 'claro'}.`
            : 'Fijo, sin importar la hora ni lo que haga el teléfono.'}
        </span>
      </Card>

      <Card>
        <strong>Tus datos</strong>
        <p className="small muted">
          Descargá todo lo que el sistema guarda de vos: perfil, mediciones, objetivos, comidas,
          estimaciones y análisis.
        </p>
        <Button variant="quiet" block onClick={() => void exportData()} disabled={busy}>
          Descargar mis datos
        </Button>
      </Card>

      <Button variant="quiet" block onClick={() => void signOut()} disabled={busy}>
        Cerrar sesión
      </Button>

      {/*
        CU-15 · la baja borra todo el contenido y ofusca los datos personales (RN-16). Es
        irreversible y el texto lo dice antes, no después.
      */}
      <Card>
        <strong>Dar de baja la cuenta</strong>
        <p className="small muted">
          Se borran tus comidas, fotos, estimaciones, mediciones, objetivos y análisis, y tus datos
          personales se ofuscan. No se puede deshacer y no hay forma de recuperarlo.
        </p>

        {confirmingDeletion ? (
          <>
            <Banner tone="danger">
              Última confirmación: esto borra todo tu historial de forma definitiva.
            </Banner>
            <div className="row">
              <Button variant="danger" onClick={() => void deleteAccount()} disabled={busy}>
                {busy ? 'Dando de baja…' : 'Dar de baja igual'}
              </Button>
              <Button variant="quiet" onClick={() => setConfirmingDeletion(false)} disabled={busy}>
                Cancelar
              </Button>
            </div>
          </>
        ) : (
          <Button variant="plain" onClick={() => setConfirmingDeletion(true)}>
            Quiero dar de baja mi cuenta
          </Button>
        )}
      </Card>
    </div>
  );
}
